<template>
  <div>
    <salvar-canal-dialog
      :is-dialog-open.sync="isEditarItemDialogOpen"
      :canal-data="canalSelecionado"
      @refetch-data="$emit('refetch-data')"
    />

    <ativar-ou-inativar-associacao-dialog
      :is-ativar-ou-inativar-associacao-dialog-open.sync="isAtivarOuInativarItemDialogOpen"
      :is-associacao-ativa-mensagem="isItemAtivoMensagem"
      :loading="isAtivarOuInativarItemLoading"
      :close-dialog="closeAtivarOuInativarDialog"
      :confirmar-ativacao-ou-inativacao-da-associacao="confirmarAtivarOuInativarItem"
    />

    <v-data-table
      :headers="headers"
      :items="items"
      :footer-props="{
        'items-per-page-text':'Linhas por página',
        'page-text': '{0}-{1} de {2}',
        'items-per-page-all-text': 'Todos',
      }"
      :loading="loading"
      loading-text="Carregando canais"
      class="elevation-1"
      no-data-text="Nenhum canal disponível"
    >
      <template #[`item.nome`]="{ item }">
        <div class="d-flex align-center">
          <v-avatar
            :color="`${$vuetify.theme.dark ? 'primary' : '#DCDCDC'}`"
            size="32"
          >
            <span :class="`${$vuetify.theme.dark ? 'white--text' : 'primary--text'} font-weight-bold`">
              {{ item.nome.toUpperCase().charAt(0) }}{{ item.nome.toUpperCase().charAt(1) }}
            </span>
          </v-avatar>

          <div class="ms-3">
            {{ item.nome }}
          </div>
        </div>
      </template>

      <template #item.ativo="{ item }">
        <v-chip
          :color="item.ativo ? 'success' : 'error'"
          outlined
        >
          {{ item.ativo ? "Ativo" : "Inativo" }}
        </v-chip>
      </template>

      <template #item.canalEmailStatusNome="{ item }">
        <v-chip
          v-if="item.canalEmailStatusId"
          :color="getCanalEmailStatusColor(item.canalEmailStatusId)"
          outlined
        >
          {{ getCanalEmailStatusText(item.canalEmailStatusId) }}
        </v-chip>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-menu
          left
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="ml-n5"
              v-bind="attrs"
              color="primary"
              v-on="on"
            >
              <v-icon>
                {{ icons.mdiDotsVertical }}
              </v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item>
              <v-list-item-title
                role="button"
                @click="openEditarItemDialog(item)"
              >
                <v-icon
                  size="20"
                  class="mr-2"
                >
                  {{ icons.mdiFileEdit }}
                </v-icon>

                <span>Editar</span>
              </v-list-item-title>
            </v-list-item>

            <v-list-item>
              <v-list-item-title
                role="button"
                @click="ativarOuInativarItem(item)"
              >
                <v-icon
                  size="20"
                  class="me-2"
                >
                  {{ item.ativo ? icons.mdiCloseThick : icons.mdiCheckBold }}
                </v-icon>
                <span>{{ item.ativo ? "Inativar" : "Ativar" }}</span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { mdiDotsVertical, mdiFileEdit, mdiCheckBold, mdiCloseThick } from '@mdi/js'
import SalvarCanalDialog from '@/views/canal/SalvarCanalDialog.vue'
import AtivarOuInativarAssociacaoDialog from '@/views/ativar-ou-inativar-associacao-dialog/AtivarOuInativarAssociacaoDialog.vue'
import CanalEmailStatusEnum from '@/models/entities/canal/CanalEmailStatusEnum'
import CanalService from '@/services/modules/CanalService'

export default {
  components: {
    SalvarCanalDialog,
    AtivarOuInativarAssociacaoDialog,
  },

  props: {
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },

  setup(_, { emit }) {
    const isEditarItemDialogOpen = ref(false)
    const canalSelecionado = ref(null)

    function openEditarItemDialog(item) {
      isEditarItemDialogOpen.value = true
      canalSelecionado.value = JSON.parse(JSON.stringify(item))
    }

    const isAtivarOuInativarItemLoading = ref(false)
    const isItemAtivo = ref(undefined)
    const isItemAtivoMensagem = ref('')
    const isAtivarOuInativarItemDialogOpen = ref(false)

    function ativarOuInativarItem(item) {
      canalSelecionado.value = JSON.parse(JSON.stringify(item))
      isItemAtivo.value = item.ativo
      isItemAtivoMensagem.value = item.ativo ? 'inativar' : 'ativar'
      isAtivarOuInativarItemDialogOpen.value = true
    }
    function closeAtivarOuInativarDialog() {
      isAtivarOuInativarItemDialogOpen.value = false
    }
    async function confirmarAtivarOuInativarItem() {
      if (isAtivarOuInativarItemLoading.value) return
      try {
        isAtivarOuInativarItemLoading.value = true
        await CanalService.ativarOuInativarItem(canalSelecionado.value.id, { ativar: !isItemAtivo.value })
        emit('refetch-data')
      } finally {
        closeAtivarOuInativarDialog()
        isAtivarOuInativarItemLoading.value = false
      }
    }

    const headers = [
      { text: 'Nome', value: 'nome' },
      { text: 'Tipo de canal', value: 'canalTipoNome' },
      { text: 'E-mail Status', value: 'canalEmailStatusNome' },
      { text: 'Status', value: 'ativo' },
      {
        text: 'Opções',
        value: 'actions',
        align: 'center',
      },
    ]

    function getCanalEmailStatusColor(canalEmailStatusId) {
      return canalEmailStatusId === CanalEmailStatusEnum.Ok ? 'success' : 'error'
    }

    function getCanalEmailStatusText(canalEmailStatusId) {
      return canalEmailStatusId === CanalEmailStatusEnum.Ok ? 'Ok' : 'Falha de autenticação'
    }

    return {
      isEditarItemDialogOpen,
      canalSelecionado,
      openEditarItemDialog,

      isAtivarOuInativarItemLoading,
      isItemAtivoMensagem,
      isAtivarOuInativarItemDialogOpen,
      ativarOuInativarItem,
      closeAtivarOuInativarDialog,
      confirmarAtivarOuInativarItem,

      getCanalEmailStatusColor,
      getCanalEmailStatusText,

      headers,
      icons: {
        mdiDotsVertical,
        mdiFileEdit,
        mdiCheckBold,
        mdiCloseThick,
      },
    }
  },
}
</script>
